import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

const navLinks = [
  { to: "/category/movie", label: "电影" },
  { to: "/category/tv", label: "电视剧" },
  { to: "/news", label: "影视资讯" },
  { to: "/about", label: "关于我们" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [keyword, setKeyword] = useState("");
  const location = useLocation();
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  const submitSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const q = keyword.trim();
    navigate(q ? `/search?q=${encodeURIComponent(q)}` : "/search");
    setMenuOpen(false);
  };

  return (
    <header
      className={`sticky top-0 z-50 w-full transition-all duration-300 ${
        scrolled
          ? "bg-background-50/95 backdrop-blur border-b border-background-200"
          : "bg-primary-950 border-b border-primary-900/60"
      }`}
    >
      <div className="w-full px-4 md:px-6 h-16 flex items-center gap-6">
        <Link
          to="/"
          className="flex items-center gap-2.5 shrink-0 group"
          aria-label="CC视频 首页"
        >
          <span className="relative w-9 h-9 rounded-md bg-accent-500 flex items-center justify-center text-primary-950 text-lg transition-transform duration-300 group-hover:scale-105">
            <i className="ri-play-fill" />
          </span>
          <span className="flex flex-col leading-none">
            <span
              className={`font-heading text-lg font-black tracking-tight ${
                scrolled ? "text-foreground-950" : "text-background-50"
              }`}
            >
              CC视频
            </span>
            <span
              className={`text-[10px] tracking-[0.2em] font-label ${
                scrolled ? "text-foreground-500" : "text-background-300"
              }`}
            >
              CINEMA CLOUD
            </span>
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-1 ml-4">
          {navLinks.map((link) => {
            const active = location.pathname.startsWith(link.to);
            return (
              <Link
                key={link.to}
                to={link.to}
                className={`px-3.5 py-2 rounded-md text-sm transition-colors whitespace-nowrap ${
                  scrolled
                    ? active
                      ? "text-accent-600 bg-accent-50 font-medium"
                      : "text-foreground-700 hover:text-foreground-950 hover:bg-background-100"
                    : active
                      ? "text-accent-300 bg-background-50/10 font-medium"
                      : "text-background-200 hover:text-background-50 hover:bg-background-50/10"
                }`}
              >
                {link.label}
              </Link>
            );
          })}
        </nav>

        <div className="flex-1" />

        <form onSubmit={submitSearch} className="hidden md:flex items-center">
          <div
            className={`flex items-center h-10 rounded-md pl-3 pr-1 w-56 xl:w-72 transition-colors border ${
              scrolled
                ? "bg-background-100 border-background-200 focus-within:border-secondary-400"
                : "bg-background-50/10 border-background-50/25 focus-within:border-accent-400"
            }`}
          >
            <i
              className={`ri-search-line text-base w-4 h-4 flex items-center justify-center ${
                scrolled ? "text-foreground-400" : "text-background-300"
              }`}
            />
            <input
              ref={inputRef}
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="搜索影片、导演、演员"
              className={`flex-1 min-w-0 bg-transparent px-2 text-sm outline-none ${
                scrolled
                  ? "text-foreground-900 placeholder:text-foreground-400"
                  : "text-background-50 placeholder:text-background-300/80"
              }`}
            />
            <button
              type="submit"
              className="h-8 px-3 rounded-md bg-accent-500 text-primary-950 text-xs font-medium whitespace-nowrap hover:bg-accent-400 transition-colors cursor-pointer"
            >
              搜索
            </button>
          </div>
        </form>

        <button
          type="button"
          onClick={() => setMenuOpen((v) => !v)}
          aria-label="打开菜单"
          className={`lg:hidden w-10 h-10 flex items-center justify-center rounded-md text-xl cursor-pointer ${
            scrolled ? "text-foreground-800" : "text-background-50"
          }`}
        >
          <i className={menuOpen ? "ri-close-line" : "ri-menu-line"} />
        </button>
      </div>

      {menuOpen && (
        <div className="lg:hidden border-t border-background-200 bg-background-50 cc-fade-in">
          <div className="px-4 md:px-6 py-4 space-y-4">
            <form onSubmit={submitSearch}>
              <div className="flex items-center h-11 rounded-md pl-3 pr-1 bg-background-100 border border-background-200">
                <i className="ri-search-line text-foreground-400 text-base w-4 h-4 flex items-center justify-center" />
                <input
                  value={keyword}
                  onChange={(e) => setKeyword(e.target.value)}
                  placeholder="搜索影片、导演、演员"
                  className="flex-1 min-w-0 bg-transparent px-2 text-sm text-foreground-900 outline-none placeholder:text-foreground-400"
                />
                <button
                  type="submit"
                  className="h-9 px-3.5 rounded-md bg-accent-500 text-primary-950 text-xs font-medium whitespace-nowrap cursor-pointer"
                >
                  搜索
                </button>
              </div>
            </form>
            <nav className="grid grid-cols-2 gap-2">
              {navLinks.map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  className="flex items-center justify-between px-3.5 py-3 rounded-md bg-background-100 text-sm text-foreground-800 cursor-pointer"
                >
                  {link.label}
                  <i className="ri-arrow-right-s-line text-foreground-400 w-4 h-4 flex items-center justify-center" />
                </Link>
              ))}
            </nav>
          </div>
        </div>
      )}
    </header>
  );
}